import { useState, useRef } from "react";
import { T } from "../lib/i18n";
import { friendlyError } from "../lib/helpers";
import { parseQuestionText, parseExcelFile, parsePDFFile, parseDocxFile, downloadExcelTemplate, EXCEL_TEMPLATE, validateQuestions } from "../lib/parsers";
import { LabelText, QuestionImages, QuestionText, RichText } from "../components/common";

// ─────────────────────────────────────────
//  IMPORT TAB
// ─────────────────────────────────────────
// Eget modul sedan TRATEditor också importerar frågor: en direktimport från
// TeacherSetup hade blivit en cirkulär import (TeacherSetup → TRATEditor).
function ImportTab({ onImport, lang, G, css }) {
  // PDF och Word är vanligast (QPS-rapporter och egna dokument), så de är förvalda.
  const [method, setMethod] = useState('doc'); // 'doc' | 'excel'
  const [showPaste, setShowPaste] = useState(false);
  // Formatet är hopfällt: det behövs innan man skriver dokumentet, inte när
  // man granskar det man just importerat. Mallen går att ladda ner ändå.
  const [showFormat, setShowFormat] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parsedQuestions, setParsedQuestions] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const t = T[lang];

  function reset() {
    setParsedQuestions(null);
    setError(null);
  }

  // Filer tolkas direkt när de valts — en separat "Tolka filen"-knapp var ett
  // extra steg utan mening. Inklistrad text tolkas på knapptryck, eftersom man
  // klistrar in och rättar i flera omgångar.
  async function handleParse(source) {
    setParsing(true);
    setError(null);

    try {
      let questions;

      if (typeof source === 'string') {
        if (!source.trim()) throw new Error('No text provided');
        questions = parseQuestionText(source);
      } else {
        if (source.size > 10 * 1024 * 1024) throw new Error(t.importFileTooLarge);
        const name = source.name.toLowerCase();
        if (method === 'excel') questions = await parseExcelFile(source);
        else if (name.endsWith('.docx')) questions = await parseDocxFile(source);
        else if (name.endsWith('.doc')) throw new Error(t.importOldWord);
        else questions = await parsePDFFile(source);
      }

      // Saknat facit stoppar inte förhandsvisningen: läraren väljer rätt svar
      // där i stället, och importknappen väntar tills alla frågor har ett.
      const validation = validateQuestions(questions, { allowMissingAnswer: true });

      if (questions.length === 0) {
        setError(t.importNoQuestions);
        setParsedQuestions(null);
      } else if (!validation.valid) {
        setError(t.importValidationError + '\n' + validation.errors.join('\n'));
        setParsedQuestions(null);
      } else {
        setParsedQuestions(questions);
      }
    } catch (err) {
      setError(err.message === t.importOldWord ? err.message : t.importError + '\n' + friendlyError(err, lang));
      setParsedQuestions(null);
    }

    setParsing(false);
  }

  function pickFile(selectedFile) {
    if (!selectedFile) return;
    setFile(selectedFile);
    reset();
    handleParse(selectedFile);
  }

  function setCorrect(qi, idx) {
    setParsedQuestions(qs => qs.map((q, i) => i === qi ? { ...q, correctAnswer: idx } : q));
  }

  const missingAnswers = (parsedQuestions || [])
    .map((q, i) => (q.correctAnswer === null || q.correctAnswer === undefined) ? i + 1 : null)
    .filter(Boolean);

  function handleImport() {
    if (parsedQuestions && parsedQuestions.length > 0 && missingAnswers.length === 0) {
      onImport(parsedQuestions);
    }
  }

  const methods = [
    { id: 'doc', title: t.importMethodDoc, desc: t.importDocDesc },
    { id: 'excel', title: t.importMethodExcel, desc: t.importExcelDesc },
  ];
  const templateBox = { background: G.surface2, border: `1px solid ${G.border}`, borderRadius: 12, padding: "14px 16px", marginTop: 16 };
  const templateLink = { display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: `1px solid ${G.gold}66`, borderRadius: 8, color: G.gold, fontSize: 13, fontWeight: 600, padding: "5px 12px", cursor: "pointer", textDecoration: "none", fontFamily: "'DM Sans', sans-serif" };
  const formatToggle = title => (
    <button
      type="button"
      onClick={() => setShowFormat(v => !v)}
      aria-expanded={showFormat}
      style={{ background: "none", border: "none", padding: 0, fontWeight: 600, fontSize: 14, color: G.text, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", gap: 8 }}
    >
      <span style={{ color: G.gold, fontSize: 13 }}>{showFormat ? '▾' : '▸'}</span>
      {title}
    </button>
  );
  const wordTemplate = `${process.env.PUBLIC_URL || ""}/mallar/${lang === 'en' ? 'tRAT-question-template.docx' : 'tRAT-fragemall.docx'}`;

  return (
    <div>
      {/* Method selector */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ ...css.heading, fontSize: 20, marginBottom: 8 }}>{t.importTitle}</h3>
        <p style={{ color: G.muted, fontSize: 14, marginBottom: 20 }}>{t.importSubtitle}</p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          {methods.map(m => {
            const active = method === m.id;
            return (
              <button
                key={m.id}
                onClick={() => { setMethod(m.id); setFile(null); setShowPaste(false); setShowFormat(false); reset(); }}
                aria-pressed={active}
                style={{
                  background: active ? G.surface2 : "transparent",
                  color: active ? G.text : G.muted,
                  border: `1.5px solid ${active ? G.gold : G.border}`,
                  borderRadius: 8,
                  padding: "12px",
                  fontSize: 14,
                  fontWeight: active ? 600 : 400,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  textAlign: "left"
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{m.title}</div>
                <div style={{ fontSize: 13, color: G.muted }}>{m.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Fil: välj eller dra hit */}
      <div style={{ marginBottom: 24 }}>
        <label style={css.label}><LabelText text={method === 'doc' ? t.importMethodDoc : t.importMethodExcel} /></label>
        <input
          ref={fileInputRef}
          type="file"
          accept={method === 'doc' ? '.pdf,.docx,.doc' : '.xlsx,.xls'}
          onChange={e => { pickFile(e.target.files?.[0]); e.target.value = ''; }}
          style={{ display: 'none' }}
          data-testid="import-file"
        />
        <div
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}
          style={{
            border: `2px dashed ${dragOver ? G.gold : G.border}`,
            borderRadius: 12,
            padding: "32px 20px",
            textAlign: "center",
            cursor: "pointer",
            background: G.surface2,
            transition: "all 0.2s"
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = G.gold}
          onMouseLeave={e => { if (!dragOver) e.currentTarget.style.borderColor = G.border; }}
        >
          {file ? (
            <div>
              <div style={{ fontSize: 14, color: G.text, marginBottom: 4 }}>✓ {file.name}</div>
              <div style={{ fontSize: 13, color: G.muted }}>{(file.size / 1024).toFixed(1)} KB</div>
            </div>
          ) : (
            <div style={{ color: G.muted, fontSize: 14 }}>{method === 'doc' ? t.importDragDropDoc : t.importDragDropExcel}</div>
          )}
        </div>

        {/* Mallen kan alltid laddas ner; formatet fälls ut vid behov */}
        {method === 'doc' && (
          <div style={templateBox}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              {formatToggle(t.importDocFormatTitle)}
              <a href={wordTemplate} download style={templateLink}>↓ {t.importDownloadWordTemplate}</a>
            </div>
            {showFormat && <>
            <pre style={{ margin: "12px 0 0", fontFamily: "'DM Mono', monospace", fontSize: 12.5, lineHeight: 1.55, color: G.text, whiteSpace: "pre-wrap" }}>{t.importDocExample}</pre>
            <p style={{ fontSize: 13, color: G.muted, margin: "10px 0 0", lineHeight: 1.5 }}>{t.importDocHint}</p>
            </>}
          </div>
        )}
        {method === 'excel' && (
          <div style={templateBox}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              {formatToggle(t.importExcelFormatTitle)}
              <button type="button" onClick={() => downloadExcelTemplate(lang)} style={templateLink}>↓ {t.importDownloadExcelTemplate}</button>
            </div>
            {showFormat && <>
            <div style={{ overflowX: "auto", marginTop: 12 }}>
              <table style={{ borderCollapse: "collapse", fontSize: 12.5, color: G.text, minWidth: "100%" }}>
                <tbody>
                  {(EXCEL_TEMPLATE[lang] || EXCEL_TEMPLATE.sv).map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => {
                        const Cell = ri === 0 ? 'th' : 'td';
                        return (
                          <Cell key={ci} style={{ border: `1px solid ${G.border}`, padding: "5px 8px", textAlign: "left", verticalAlign: "top", fontWeight: ri === 0 ? 700 : 400, background: ri === 0 ? G.surface : "transparent", whiteSpace: ci === 0 || ci === row.length - 1 ? "normal" : "nowrap" }}>
                            {cell}
                          </Cell>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: 13, color: G.muted, margin: "10px 0 0", lineHeight: 1.5 }}>{t.importExcelHint}</p>
            </>}
          </div>
        )}

        {/* Reserv för Google Docs och annat som inte är en fil */}
        {method === 'doc' && (
          <div style={{ marginTop: 14 }}>
            <button
              type="button"
              onClick={() => setShowPaste(v => !v)}
              aria-expanded={showPaste}
              style={{ background: "none", border: "none", padding: 0, color: G.gold, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
            >
              {showPaste ? '▾' : '▸'} {t.importOrPaste}
            </button>
            {showPaste && (
              <div style={{ marginTop: 10 }}>
                <textarea
                  aria-label={t.importOrPaste}
                  style={{ ...css.textarea, minHeight: 200, fontFamily: "'DM Mono', monospace", fontSize: 13 }}
                  placeholder={t.importPasteHere}
                  value={textInput}
                  onChange={e => { setTextInput(e.target.value); reset(); }}
                />
                {!parsedQuestions && (
                  <button
                    onClick={() => { setFile(null); handleParse(textInput); }}
                    disabled={parsing || !textInput.trim()}
                    style={{ ...css.btnPrimary, opacity: parsing || !textInput.trim() ? 0.5 : 1, marginTop: 10 }}
                  >
                    {parsing ? t.importParsing : t.importParseText}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {!parsedQuestions && file && parsing && (
        <div style={{ color: G.muted, fontSize: 14, marginBottom: 20 }}>{t.importParsing}</div>
      )}

      {/* Error */}
      {error && (
        <div style={{ background: G.red + "18", border: `2px solid ${G.red}44`, borderRadius: 12, padding: "14px 18px", marginBottom: 20 }}>
          <div style={{ fontSize: 14, color: G.red, whiteSpace: "pre-line" }}>{error}</div>
        </div>
      )}
      
      {/* Preview */}
      {parsedQuestions && parsedQuestions.length > 0 && (
        <div>
          <div style={{ background: G.correct + "18", border: `2px solid ${G.correct}44`, borderRadius: 12, padding: "14px 18px", marginBottom: 20 }}>
            <div style={{ fontSize: 14, color: G.correct, fontWeight: 600 }}>
              {t.importSuccess.replace('{count}', parsedQuestions.length)}
            </div>
          </div>
          
          {missingAnswers.length > 0 && (
            <div role="alert" style={{ background: G.gold + "18", border: `2px solid ${G.gold}55`, borderRadius: 12, padding: "14px 18px", marginBottom: 20, fontSize: 14, color: G.text }}>
              {t.importMissingAnswers.replace('{list}', missingAnswers.join(', '))}
            </div>
          )}

          <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
            <button
              onClick={handleImport}
              disabled={missingAnswers.length > 0}
              style={{ ...css.btnPrimary, flex: 1, opacity: missingAnswers.length > 0 ? 0.5 : 1 }}
            >
              {t.importConfirm.replace('{count}', parsedQuestions.length)}
            </button>
            <button
              onClick={() => { reset(); setTextInput(''); setFile(null); }}
              style={{ ...css.btnSecondary, flex: 1, marginTop: 0 }}
            >
              {t.importCancel}
            </button>
          </div>

          <div style={{ ...css.label, marginBottom: 4 }}>{t.importPreview}</div>
          <p style={{ color: G.muted, fontSize: 13, margin: "0 0 12px" }}>{t.importPickHint}</p>

          <div style={{ maxHeight: 400, overflowY: "auto", background: G.surface2, borderRadius: 12, padding: "16px" }}>
            {parsedQuestions.map((q, i) => (
              <div key={i} style={{ marginBottom: 20, paddingBottom: 20, borderBottom: i < parsedQuestions.length - 1 ? `1px solid ${G.border}` : 'none' }}>
                <div style={{ fontWeight: 600, marginBottom: 8, color: G.text }}>
                  {t.importQuestionPreview.replace('{num}', i + 1)}
                </div>
                <QuestionText text={q.text} style={{ fontSize: 14, color: G.text, marginBottom: 8, lineHeight: 1.5 }} />
                {/* Figurer ur PDF:en — förhandsvisning innan import */}
                <QuestionImages images={q.images} G={G} lang={lang} maxHeight={260} />
                {/* Alternativen är knappar: ett klick gör alternativet till rätt svar,
                    för frågor utan facit och för att rätta en feltolkning */}
                <div role="radiogroup" aria-label={t.importQuestionPreview.replace('{num}', i + 1)} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {Object.entries(q.options).map(([key, value], idx) => {
                    const isCorrect = q.correctAnswer === idx;
                    return (
                      <button
                        key={key}
                        type="button"
                        role="radio"
                        aria-checked={isCorrect}
                        onClick={() => setCorrect(i, idx)}
                        style={{
                          textAlign: "left", background: isCorrect ? G.correct + "18" : "transparent",
                          border: `1px solid ${isCorrect ? G.correct + "66" : "transparent"}`, borderRadius: 6,
                          padding: "4px 8px", fontSize: 13, lineHeight: 1.5, cursor: "pointer",
                          color: isCorrect ? G.correct : G.muted, fontWeight: isCorrect ? 700 : 400,
                          fontFamily: "inherit",
                        }}
                      >
                        {isCorrect ? "✓ " : ""}{key}: {value}
                      </button>
                    );
                  })}
                </div>
                {q.correctAnswer === null || q.correctAnswer === undefined ? (
                  <div style={{ fontSize: 13, color: G.gold, fontWeight: 600, marginTop: 8 }}>{t.importNoAnswer}</div>
                ) : (
                  <div style={{ fontSize: 13, color: G.correct, marginTop: 8 }}>
                    {t.importCorrectAnswer.replace('{answer}', String.fromCharCode(65 + q.correctAnswer))}
                  </div>
                )}
                {q.rationale && (
                  <div style={{ fontSize: 13, color: G.muted, marginTop: 8, lineHeight: 1.5 }}>💡 <RichText text={q.rationale} /></div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export { ImportTab };
